import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import axios from 'axios';

import en from './en.json';
import fr from './fr.json';
import de from './de.json';

// Boot language for the pre-auth pages (login, forgot-password, legal).
// Must NOT read localStorage — the persisted `regure_locale_settings` is the
// PREVIOUS user's preference, so keying off it made the login page render in
// French for every browser that had ever hosted a French session. The
// resolved user/tenant preference kicks in after login via the useEffect in
// navigation/index.js and switches the language then.
const supportedLngs = ['en', 'fr', 'de'];
const browserLng = navigator.language?.split('-')[0];
const bootLng = supportedLngs.includes(browserLng) ? browserLng : 'en';

const BUNDLES = { en, fr, de };

// ─── Tenant vocabulary ────────────────────────────────────────
//
// What a tenant calls the core Regure objects reaches the interface as
// i18next interpolation variables rather than as string replacement or
// per-screen conditionals. A bundle string says "No {{claims}} yet" and
// the tenant's word arrives without the screen knowing this system exists.
//
// Two layers, in this order:
//
//   1. the CURRENT LOCALE's defaults, from the bundle's `vocabulary` block
//      — so an unconfigured French tenant reads "Sinistres", not "Claims"
//   2. the tenant's own overrides, applied per locale where the tenant has
//      supplied one and otherwise across every language:
//        overrides[term].locales[lng] -> overrides[term] -> bundle default
//
//      Regure still does not translate a tenant's business vocabulary for
//      them — that would be a guess — but a tenant running the interface in
//      more than one language can now say what they call the object in each.
//      Without this layer one literal override had to serve all three
//      interfaces, so MOK's German UI rendered English tenant nouns inside
//      German sentences.
//
//      Overrides with no `locales` key behave exactly as before, which is what
//      keeps every tenant configured under the old shape where it was.
//
// This object is handed to i18next ONCE as `interpolation.defaultVariables`
// and thereafter mutated in place. The interpolator holds the same
// reference, so updates are visible immediately without re-initialising it.
const vocabularyVars = {};

/** Overrides last received from the tenant config; re-applied on language change. */
let tenantOverrides = {};

const TERMS = ['claim', 'client', 'broker', 'riskCarrier', 'policy', 'offer', 'document', 'invoice'];
const PLURAL_VAR = {
  claim: 'claims', client: 'clients', broker: 'brokers', riskCarrier: 'riskCarriers',
  policy: 'policies', offer: 'offers', document: 'documents', invoice: 'invoices',
};

function rebuildVocabulary() {
  const lng = supportedLngs.includes(i18n.language) ? i18n.language : 'en';
  const defaults = BUNDLES[lng]?.vocabulary || BUNDLES.en.vocabulary || {};

  for (const term of TERMS) {
    const base = defaults[term] || {};
    const over = tenantOverrides[term] || {};
    // Per-locale override first, then the tenant's language-independent
    // override, then the current bundle's own word. Resolution is per FORM, so
    // a tenant supplying only a German plural keeps its base singular.
    const localised = over.locales?.[lng] || {};
    const one = (localised.one || over.one || base.one || '').trim();
    const other = (localised.other || over.other || base.other || '').trim();
    const pluralVar = PLURAL_VAR[term];

    vocabularyVars[term] = one;
    vocabularyVars[pluralVar] = other;
    // Mid-sentence forms for locales that lowercase common nouns. German
    // strings capitalise nouns and use the base variable instead.
    vocabularyVars[`${term}Lower`] = one.toLocaleLowerCase(lng);
    vocabularyVars[`${pluralVar}Lower`] = other.toLocaleLowerCase(lng);
  }
}

/**
 * Apply the tenant's configured terms, as returned by GET /tenant/config
 * (`vocabularyOverrides` — only what the tenant actually set).
 *
 * Called on login and after a Settings save. Re-emits `languageChanged`
 * because that is the event react-i18next's useTranslation subscribes to;
 * without it, mounted screens keep their previously interpolated strings
 * until something else re-renders them.
 */
export function applyTenantVocabulary(overrides) {
  tenantOverrides = overrides && typeof overrides === 'object' ? overrides : {};
  rebuildVocabulary();
  i18n.emit('languageChanged', i18n.language);
}

/** Drop tenant terms on logout so the next user never inherits them. */
export function resetTenantVocabulary() {
  applyTenantVocabulary({});
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      de: { translation: de },
    },
    lng: bootLng,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
      // Shared reference — see the note above.
      defaultVariables: vocabularyVars,
    },
  });

// Seed before anything renders, so a pre-auth screen using a vocabulary
// placeholder never paints an empty word.
rebuildVocabulary();

// Switching language must re-resolve the defaults underneath the tenant's
// overrides; the overrides themselves are language-independent.
i18n.on('languageChanged', () => { rebuildVocabulary(); });

// Tell the API which language the user is reading, so a validation error
// comes back in it (services/referenceGuard.js answers fr / de / en). The
// browser's own Accept-Language is the OS setting, not the Regure UI
// language — a French screen on an English Windows used to get English.
const setApiLanguage = (lng) => {
  // Guarded: tests replace axios with an automock that has no defaults.
  const common = axios?.defaults?.headers?.common;
  if (common) common['Accept-Language'] = String(lng || 'en').slice(0, 2);
};
setApiLanguage(i18n.language);
i18n.on('languageChanged', setApiLanguage);

export default i18n;
